import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { useI18n } from '../../../i18n';
import { useAuthFeatures, CountUp, useCommunityCount } from './loginShared';
import RotatingTagline from './RotatingTagline';
import freeBlueAvatar from '../../../media/free_blue.webp';
import freePinkAvatar from '../../../media/free_pink.webp';
import freeYellowAvatar from '../../../media/free_yellow.webp';

const communityAvatars = [freeBlueAvatar, freePinkAvatar, freeYellowAvatar];

/* Desktop-only overlay on top of the 3D scene: brand, headline,
 * feature highlights and community proof. Container ignores pointer
 * events so the camera parallax keeps tracking the mouse. */
export default function LoginInfo() {
  const { t } = useI18n();
  const features = useAuthFeatures();
  const userCount = useCommunityCount();

  return (
    <div className="pointer-events-none flex flex-col justify-between h-full py-2 max-w-[620px]">
      {/* Brand */}
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex items-center gap-3"
      >
        <img
          src="/logo_black.webp"
          alt="MyKart"
          fetchPriority="high"
          decoding="async"
          className="w-12 h-12 drop-shadow-lg"
        />
        <div>
          <span className="text-xl font-extrabold tracking-tight" style={{ color: 'var(--text-primary)' }}>
            MyKart.pl
          </span>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            {t('Twój osobisty dziennik kartingowy')}
          </p>
        </div>
      </motion.div>

      {/* Headline — kept high so it never covers the 3D kart below */}
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
        className="mt-10 mb-auto"
      >
        <p
          className="text-[11px] font-semibold uppercase tracking-[0.35em] mb-4"
          style={{ color: 'var(--text-muted)' }}
        >
          {t('Karting bez zgadywania')}
        </p>
        <h1
          className="text-5xl xl:text-6xl font-black italic tracking-tight leading-[1.02] uppercase"
          style={{ color: 'var(--text-primary)' }}
        >
          {t('Każde okrążenie')}
          <br />
          <span style={{ color: 'var(--accent)' }}>{t('się liczy.')}</span>
        </h1>
        <RotatingTagline className="mt-5" />
      </motion.div>

      <div className="mt-8">
        {/* Feature highlights — same design language as the journey HUD/seams */}
        <div
          className="flex items-start justify-between gap-6 max-w-[560px] pt-4"
          style={{ borderTop: '1px solid var(--glass-border)' }}
        >
          {features.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.45 + i * 0.08 }}
              className="flex flex-col gap-1.5 min-w-0"
            >
              <span className="flex items-center gap-1.5">
                <f.icon size={13} style={{ color: f.color, opacity: 0.85 }} />
                <span
                  className="text-[10px] font-bold uppercase tracking-[0.14em] whitespace-nowrap"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {f.text}
                </span>
              </span>
              <span className="text-[10px] leading-snug" style={{ color: 'var(--text-muted)' }}>
                {f.desc}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Social proof + CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-6 flex items-center gap-6 flex-wrap"
        >
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {communityAvatars.map((avatarSrc, i) => (
                <motion.img
                  key={i}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.9 + i * 0.1, type: 'spring' }}
                  src={avatarSrc}
                  alt=""
                  className="w-8 h-8 rounded-full border-2 object-cover"
                  style={{ borderColor: 'var(--surface-card)' }}
                />
              ))}
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-extrabold" style={{ color: 'var(--text-primary)' }}>
                {userCount != null
                  ? <><CountUp value={userCount + 200} />+</>
                  : '200+'}{' '}
                <span style={{ color: 'var(--accent)' }}>{t('kierowców')}</span>
              </span>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {t('Dołącz do rosnącej społeczności')}
              </span>
            </div>
          </div>

          <Link
            to="/dla-kartingowcow"
            className="group pointer-events-auto inline-flex items-center gap-2 rounded-2xl pl-4 pr-3 py-2.5 text-sm font-semibold transition-all hover:-translate-y-0.5"
            style={{ background: 'var(--accent-glow)', color: 'var(--accent)', border: '1px solid rgba(34,197,94,0.25)' }}
          >
            {t('auth.marketing.link')}
            <span
              className="w-6 h-6 rounded-lg flex items-center justify-center transition-transform group-hover:translate-x-0.5"
              style={{ background: 'rgba(34,197,94,0.18)' }}
            >
              <ArrowUpRight size={14} />
            </span>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
